import { MoreVert } from '@mui/icons-material';
import { Box, IconButton, Popover, TableCell } from '@mui/material';
import React, { useState } from 'react';
import { useListContext } from '../ListContext';
import { stickyActionsCellSx, stickyActionsCompactSx } from '../stickyActions';

const ActionMenu = (props: ActionMenuProps) => {
  const { refetch } = useListContext();
  const { align = 'center', children, variant = 'cell' } = props;
  const [open, setOpen] = useState<(EventTarget & HTMLButtonElement) | null>(null);

  const handleOpenMenu: React.MouseEventHandler<HTMLButtonElement> = (event) => {
    setOpen(event.currentTarget);
  };

  const handleCloseMenu = () => {
    setOpen(null);
  };

  return (
    <>
      {variant === 'standalone' ? (
        <TableCell align={align} sx={stickyActionsCellSx}>
          <Box display="inline-flex" alignItems="center" justifyContent={align === 'right' ? 'flex-end' : 'flex-start'}>
            {React.Children.map(children, (field, index) => {
              const element = field as React.ReactElement;
              return (
                <element.type
                  {...(element.props as object)}
                  key={index}
                  onClose={handleCloseMenu}
                  onSuccess={() => {
                    handleCloseMenu();
                    typeof refetch === 'function' && refetch();
                  }}
                />
              );
            })}
          </Box>
        </TableCell>
      ) : (
        <TableCell align={align} sx={stickyActionsCompactSx}>
          <IconButton
            onClick={handleOpenMenu}
            aria-label="Actions"
            sx={{
              color: 'text.primary',
              width: 32,
              height: 32
            }}
          >
            <MoreVert />
          </IconButton>
        </TableCell>
      )}
      <Popover
        open={!!open}
        anchorEl={open}
        onClose={handleCloseMenu}
        anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{
          sx: { width: 200 }
        }}
      >
        {React.Children.map(children, (field, index) => {
          const element = field as React.ReactElement;
          return (
            <element.type
              {...(element.props as object)}
              key={index}
              onClose={handleCloseMenu}
              onSuccess={() => {
                handleCloseMenu();
                typeof refetch === 'function' && refetch();
              }}
              variant="cell"
            />
          );
        })}
      </Popover>
    </>
  );
};

type ActionMenuProps = {
  label?: string;
  width?: string;
  align?: 'center' | 'left' | 'right' | 'inherit' | 'justify' | undefined;
  children?: Array<React.ReactElement> | React.ReactElement;
  /** 'standalone' for inline actions (default), 'cell' for popover menu in tables */
  variant?: 'cell' | 'standalone';
  defaultHidden?: boolean;
  hideable?: boolean;
};

type ActionMenuComponent = typeof ActionMenu & {listColumn?: 'actions'};

(ActionMenu as ActionMenuComponent).listColumn = 'actions';

export default ActionMenu;
